import React, { useRef } from 'react'
import useSize from 'hooks/useSize'
import { scaleLinear, ScaleLinear } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'
import { formatSwissDate } from 'components/globalDrawFunctions'

import './styles.scss'

const textOffset = 12
const markerRad = 1.5
type ChartProps = {
  data: cantonsDataData[]
  yProp: string
  xDomain: [number, number]
  yDomain: [number, number]
  className: string
  yTicks: { text: number[]; lines: number[] }
  isFirst: boolean
  noData: boolean
  start: string
}
export const Chart: React.FC<ChartProps> = ({
  data,
  yProp,
  start,
  xDomain,
  yDomain,
  className,
  yTicks,
  isFirst,
  noData
}) => {
  const [container, size] = useSize()
  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const height = 80
  const gutterTop = 2
  const gutterRight = size.isMobile ? 30 : 30
  const gutterLeft = 8
  const gutterBottom = 20
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size]
  )
  const yFunction = (d: cantonsDataData) =>
    yAxisScale.range()[0] - yAxisScale(d[yProp])

  const xFunction = (d: cantonsDataData, i: number) => xAxisScale(i)
  const lineFunction = React.useMemo(
    () =>
      line()
        .x(xFunction)
        .y(yFunction)
        .defined(d => d !== null)
        .curve(curveBasis),
    [size]
  )
  const splitToChunks = (array, parts) => {
    let result = []
    for (let i = parts; i > 0; i--) {
      result.push(array.splice(0, Math.ceil(array.length / i)))
    }
    return result
  }
  const getMaxValues = arr => {
    let max = Math.max(...arr)
    let index = arr.findIndex(f => f === max)
    return [max, index]
  }
  const maxValues = getMaxValues(data.map(m => +m[yProp]))
  console.log(maxValues)
  return (
    <div className={'lineonly ' + className}>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          <defs>
            <marker
              id={yProp}
              className='endMarker'
              markerWidth={markerRad * 2}
              markerHeight={markerRad * 2}
              refX={markerRad}
              refY={markerRad}
            >
              <circle
                cx={markerRad}
                cy={markerRad}
                r={markerRad}
                stroke='none'
              />
            </marker>
          </defs>

          <YTicks
            data={yTicks}
            x={xAxisScale}
            y={yAxisScale}
            showText={isFirst}
          />
          {data.length > 0 && !noData && (
            <>
              <path d={lineFunction(data)} />

              <LastValue
                last={data[data.length - 1][yProp]}
                x={xAxisScale(data.length - 1)}
                y={yAxisScale}
                yProp={yProp}
                offset={10}
                align={'left'}
                circle
              />
              {maxValues[0] > 0 &&
                maxValues[0] !== data[data.length - 1][yProp] && (
                  <LastValue
                    last={maxValues[0]}
                    x={xAxisScale(maxValues[1])}
                    y={yAxisScale}
                    yProp={yProp}
                    circle
                    offset={10}
                    align={'right'}
                  />
                )}
            </>
          )}
          {noData && (
            <text
              x={
                xAxisScale.range()[0] +
                (xAxisScale.range()[1] - xAxisScale.range()[0]) / 2
              }
              y={yAxisScale.range()[0] - 4}
              className='nodata'
              textAnchor='middle'
            >
              Keine Daten
            </text>
          )}
          {!noData && (
            <>
              <text
                className='ticktext x'
                x={xAxisScale.range()[0]}
                y={yAxisScale.range()[0] + 17}
              >
                {formatSwissDate(start, 'short')}
              </text>
              <text
                className='ticktext x'
                x={xAxisScale(data.length)}
                y={yAxisScale.range()[0] + 17}
                textAnchor='end'
              >
                {formatSwissDate(data[data.length - 1].date, 'short')}
              </text>
            </>
          )}
        </svg>
      </div>
    </div>
  )
}
const YTicks = ({
  data,
  x,
  y,
  showText = true
}: {
  data: { text: number[]; lines: number[] }
  x: ScaleLinear<number, number>
  y: ScaleLinear<number, number>
  showText?: boolean
}) => {
  return (
    <>
      {data.lines.map((t, i) => {
        return (
          <line
            key={i}
            y1={y(t)}
            y2={y(t)}
            x1={x.range()[0]}
            x2={x.range()[1]}
            className='tickline'
          />
        )
      })}
      {showText &&
        data.text.map((t, i) => {
          return (
            <text
              key={i}
              x={x.range()[0] + 3}
              y={y.range()[0] - y(t) + textOffset}
              className='ticktext'
            >
              {t}
            </text>
          )
        })}
    </>
  )
}

const LastValue = ({
  last,
  yProp,
  x,
  y,
  circle = false,
  offset = 0,
  align = 'left'
}: {
  last: number
  yProp: string
  x: number
  y: ScaleLinear<number, number>
  circle?: boolean
  offset?: number
  align: 'left' | 'right'
}) => {
  const lastVal = {
    x: x,
    y: y.range()[0] - y(last) - offset,
    text: last
  }
  return (
    <>
      <text
        x={lastVal.x}
        y={lastVal.y}
        textAnchor={align === 'left' ? 'start' : 'end'}
        className='lastVal'
      >
        {lastVal.text}
      </text>
      {/* <line
        x1={lastVal.x - 0.5}
        x2={lastVal.x - 0.5}
        y1={lastVal.y + 3}
        y2={y.range()[0]}
        className='tickline overlay'
        opacity={.4}
      /> */}
      {circle && (
        <circle
          cx={lastVal.x}
          cy={lastVal.y + offset}
          r={3}
          fill='#f00'
          className={'lastVal ' + yProp}
        />
      )}
    </>
  )
}
